import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const data = await login(form.email, form.password);
      toast.success(`Welcome back, ${data.user.name.split(' ')[0]}!`);
      navigate(data.user.role === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-grid">
        <div className="surface-panel glow-surface auth-side grid-bg">
          <div>
            <Link to="/" className="auth-brand">
              <div className="brand-mark" />
              <div className="brand-copy">
                <div className="brand-title">CarbonX</div>
                <div className="brand-subtitle">Premium carbon workflow</div>
              </div>
            </Link>

            <h1 className="auth-title">
              A calmer, cleaner sign-in flow for carbon trading teams.
            </h1>
            <p className="auth-copy">
              The auth experience now mirrors the rest of the product: minimal chrome, stronger hierarchy, and a clearer path into the marketplace, trade desk, and compliance workflows.
            </p>
          </div>

          <div className="auth-feature-grid">
            <div className="auth-feature">
              <div className="stat-label">Marketplace</div>
              <div className="feature-title">Discover inventory quickly</div>
              <div className="stat-sub">Cleaner cards and faster scan patterns for buyers.</div>
            </div>
            <div className="auth-feature">
              <div className="stat-label">Compliance</div>
              <div className="feature-title">Rule-aligned modeling</div>
              <div className="stat-sub">Current compensation logic and more honest market references.</div>
            </div>
            <div className="auth-feature">
              <div className="stat-label">Portfolio</div>
              <div className="feature-title">Higher signal dashboard</div>
              <div className="stat-sub">Balances, reference trends, and alerts first.</div>
            </div>
            <div className="auth-feature">
              <div className="stat-label">Mobile ready</div>
              <div className="feature-title">Shared design direction</div>
              <div className="stat-sub">Structured to scale into Android and iOS apps.</div>
            </div>
          </div>
        </div>

        <div className="surface-panel auth-panel">
          <div className="auth-header">
            <div className="section-tag">Sign in</div>
            <h2 className="auth-title-small">Access your trading workspace</h2>
            <p className="auth-copy-small">
              Use your account credentials or pick a demo profile to explore the redesigned experience.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email address</label>
              <input
                className="form-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@company.com"
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-input"
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPass((value) => !value)}
                  style={{ position: 'absolute', right: '0.9rem', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: 'var(--muted2)', cursor: 'pointer', fontSize: '0.78rem' }}
                >
                  {showPass ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: '0.4rem' }}>
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <div style={{ marginTop: '1.5rem' }} className="stat-sub">
            No account yet? <Link to="/signup" style={{ color: 'var(--neon-green)', textDecoration: 'none' }}>Create one</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
